/**
 * FieldTypeMenu - Notion-style searchable popover of field types.
 *
 * Shared between two triggers:
 * 1. The per-block "+" button (`AddFieldButton`) on the left rail of every card.
 * 2. The slash command (`/`) typed inside a `FieldCard` textarea.
 *
 * Common answer formats appear directly in the shared capability catalogue;
 * search also reveals rating and Information presets. Calendar also obeys
 * its release flag and the one-per-conversation rule; File upload obeys the
 * file storage release flag.
 *
 * Keyboard model:
 * - The wrapper handles Esc → close. Arrow Up/Down navigate the filtered list.
 * - Enter activates the highlighted option. The slash-command path injects the
 *   already-typed query as `initialQuery` so users can type past the slash and
 *   see the list narrow without having to click the search box.
 *
 * The menu is intentionally NOT a Headless UI Menu - that primitive auto-closes
 * on focus loss, but we need the search input to keep focus while the user
 * types in either the textarea (for slash) or the field directly (for click).
 *
 * @module components/form-builder/FieldTypeMenu
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IconId, IconAlignLeft, IconLink, IconPhone, IconHash, IconMail, IconListCheck, IconCircleCheck, IconStar, IconMessageQuestion, IconInfoCircle, IconCalendar, IconUpload, IconLayoutRows } from '@tabler/icons-react';
import { createPortal } from 'react-dom';
import { Transition } from '@headlessui/react';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { GenerationRequestError, resolveGenerationErrorMessage } from '../../lib/generation-errors';
import { useReleaseFeature } from '@/lib/release-flags';
import type { FieldType } from '../../types';
import { FORM_FIELD_CAPABILITIES, type FormFieldFormat } from '../../lib/form-capabilities';
import { AnimatedPresence } from '../ui/AnimatedPresence';
import { ButtonContent } from '../ui/ButtonContent';

/** A single row in the field-type list. */
interface FieldTypeOption {
  type: FieldType | 'section';
  format?: FormFieldFormat;
  shortcuts?: readonly string[];
  label: string;
  description: string;
  /** Disabled legacy rows are hidden rather than advertised before release. */
  enabled: boolean;
  /** Optional icon glyph rendered to the left of the label. */
  icon: React.ReactNode;
}

/** Props accepted by `FieldTypeMenu`. */
export interface FieldTypeMenuProps {
  /** When false, the popover exits before unmounting. */
  isOpen: boolean;
  /** Optional starting query - used by slash-command to forward typed text. */
  initialQuery?: string;
  /**
   * If true, the search input does NOT render (slash-command mode forwards
   * keystrokes from the host textarea instead). Defaults to false - the
   * stand-alone "+ button" trigger embeds the input.
   */
  hideSearchInput?: boolean;
  /** Live query - used in slash mode to filter as the user types past `/`. */
  query?: string;
  /** Called with a storage type and, for versioned conversations, its format. */
  onSelect: (type: FieldType, format?: FormFieldFormat) => void;
  /** Offer a structural section only when the host supports section authoring. */
  onAddSection?: () => void;
  /**
   * When true, the one-per-campaign calendar block already exists, so the
   * "Calendar" option is hidden from the menu (the cheapest enforcement point
   * for the milestone-1 single-calendar rule).
   */
  hasCalendarBlock?: boolean;
  /** Called when the user presses Esc or clicks outside. */
  onClose: () => void;
  /** Wrapper element to register outside-click against (defaults to self). */
  outsideClickIgnoreRef?: React.RefObject<HTMLElement | null>;
  /** Anchor for a viewport-positioned portal outside scrolling ancestors. */
  anchorRef?: React.RefObject<HTMLElement | null>;
  /** Generate one question or Information block from the creator's description. */
  onGenerate?: (description: string, signal: AbortSignal) => Promise<void>;
  /** Optional className for positioning by the parent. */
  className?: string;
}

/**
 * Searchable, keyboard-navigable list of field types. Used by both the per-card
 * "+" rail and the inline slash command inside a question textarea.
 */
export const FieldTypeMenu: React.FC<FieldTypeMenuProps> = ({
  isOpen,
  initialQuery = '',
  hideSearchInput = false,
  query,
  onSelect,
  onAddSection,
  hasCalendarBlock = false,
  onClose,
  outsideClickIgnoreRef,
  className,
  anchorRef,
  onGenerate,
}) => {
  const calendarBookingsEnabled = useReleaseFeature('calendarBookings');
  const fileStorageEnabled = useReleaseFeature('fileStorage');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [internalQuery, setInternalQuery] = useState(initialQuery);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [position, setPosition] = useState<React.CSSProperties>({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const generationRef = useRef<AbortController | null>(null);

  useEffect(() => () => generationRef.current?.abort(), []);

  // Cancellation takes effect immediately, independently of the visual exit.
  useEffect(() => {
    if (!isOpen) {
      generationRef.current?.abort();
      generationRef.current = null;
      return;
    }
    setInternalQuery(initialQuery);
    setGenerationError(null);
    setIsGenerating(false);
  }, [isOpen, initialQuery]);

  // Portalling removes ancestor clipping. Reposition when either scroll area
  // moves, and give the list only the space available on the roomier side.
  useLayoutEffect(() => {
    if (!isOpen || !anchorRef) return undefined;
    const updatePosition = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop ?? 0;
      const viewportLeft = viewport?.offsetLeft ?? 0;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      const viewportWidth = viewport?.width ?? window.innerWidth;
      const width = Math.min(320, viewportWidth - 16);
      const below = Math.max(0, viewportTop + viewportHeight - rect.bottom - 14);
      const above = Math.max(0, rect.top - viewportTop - 14);
      const openAbove = below < 480 && above > below;
      setPosition({
        position: 'fixed', width,
        left: Math.max(viewportLeft + 8, Math.min(rect.left, viewportLeft + viewportWidth - width - 8)),
        ...(openAbove ? { bottom: window.innerHeight - rect.top + 6 } : { top: Math.max(viewportTop + 8, rect.bottom + 6) }),
        maxHeight: Math.min(560, openAbove ? above : below),
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    window.visualViewport?.addEventListener('resize', updatePosition);
    window.visualViewport?.addEventListener('scroll', updatePosition);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
      window.visualViewport?.removeEventListener('resize', updatePosition);
      window.visualViewport?.removeEventListener('scroll', updatePosition);
    };
  }, [isOpen, anchorRef]);

  /** One request per menu, discarded if the creator dismisses it. */
  const handleGenerate = useCallback(async () => {
    const description = (query ?? internalQuery).trim();
    if (!onGenerate || !description || generationRef.current) return;
    const controller = new AbortController();
    generationRef.current = controller;
    setIsGenerating(true);
    setGenerationError(null);
    try {
      await onGenerate(description, controller.signal);
      if (!controller.signal.aborted) onClose();
    } catch (error) {
      if (!controller.signal.aborted) {
        if (error instanceof GenerationRequestError) {
          // Copy follows the code and the retry decision. The worker captured
          // what it classified; a failure the client detected is captured here.
          if (!error.serverReported) captureException(error, 'FieldTypeMenu.generateQuestion', { code: error.code });
          setGenerationError(resolveGenerationErrorMessage('question', error.code, error.retryable));
        } else {
          captureException(error instanceof Error ? error : new Error(String(error)), 'FieldTypeMenu.generateQuestion');
          setGenerationError(resolveGenerationErrorMessage('question', 'failed'));
        }
      }
    } finally {
      if (!controller.signal.aborted) {
        generationRef.current = null;
        setIsGenerating(false);
      }
    }
  }, [internalQuery, query, onGenerate, onClose]);

  // The effective filter source: slash-command mode forwards `query` directly;
  // click mode reads the embedded input.
  const effectiveQuery = (query !== undefined ? query : internalQuery).trim().toLowerCase();

  const filtered = useMemo(() => {
    // Calendar retains its existing availability and one-per-conversation rule.
    const formats: FormFieldFormat[] = ['text', 'url', 'number', 'name', 'email', 'phone', 'file_upload', 'choice_one', 'choice_many', 'scale', 'explore', 'information_text', 'calendar'];
    const labels: Partial<Record<FormFieldFormat, string>> = { choice_one: 'Single choice', choice_many: 'Multiple choice', scale: 'Rating', information_text: 'Information' };
    const icons = { Answer: IconAlignLeft, Choice: IconListCheck, Rating: IconStar, Information: IconInfoCircle, Calendar: IconCalendar, Files: IconUpload };
    const typedOptions: FieldTypeOption[] = (effectiveQuery
      ? FORM_FIELD_CAPABILITIES
      : formats.map((format) => FORM_FIELD_CAPABILITIES.find((capability) => capability.format === format)!))
      .map((capability) => {
        const Icon = capability.format === 'name' ? IconId : capability.format === 'url' ? IconLink : capability.format === 'phone' ? IconPhone : capability.format === 'file_upload' ? IconUpload : capability.format === 'number' ? IconHash : capability.format === 'email' ? IconMail
          : capability.format === 'explore' ? IconMessageQuestion : capability.format === 'choice_one' ? IconCircleCheck : icons[capability.family];
        return {
          type: capability.type, format: capability.format, shortcuts: capability.shortcuts,
          label: effectiveQuery && capability.family !== 'Files' && capability.family !== 'Calendar' ? `${capability.family} · ${capability.label}` : labels[capability.format] ?? capability.label,
          description: '', enabled: true, icon: <Icon aria-hidden="true" className="h-4 w-4" />,
        };
      });
    if (onAddSection) {
      const sectionOption: FieldTypeOption = { type: 'section', label: 'Section', description: '', shortcuts: ['group'], enabled: true, icon: <IconLayoutRows aria-hidden="true" className="h-4 w-4" /> };
      // Explicit section authoring takes precedence over legacy Information search aliases.
      if (effectiveQuery) typedOptions.unshift(sectionOption);
      else typedOptions.splice(typedOptions.findIndex(option => option.format === 'information_text') + 1, 0, sectionOption);
    }
    const visible = typedOptions.filter(
      (o) =>
        o.enabled &&
        !(o.type === 'calendar_booking' && (!calendarBookingsEnabled || hasCalendarBlock)) &&
        !(o.type === 'file_upload' && !fileStorageEnabled),
    );
    if (!effectiveQuery) return visible;
    return visible.filter((o) =>
      [o.label, ...(o.shortcuts ?? [])].some((label) => label.toLowerCase().includes(effectiveQuery)),
    );
  }, [calendarBookingsEnabled, effectiveQuery, fileStorageEnabled, hasCalendarBlock, onAddSection]);

  // Reset highlight to the first enabled row whenever the filter changes so
  // Enter always activates a sensible default.
  useEffect(() => {
    const firstEnabled = filtered.findIndex((o) => o.enabled);
    setHighlightIndex(firstEnabled >= 0 ? firstEnabled : 0);
  }, [filtered]);

  useEffect(() => {
    containerRef.current?.querySelector<HTMLElement>('[role="option"][aria-selected="true"]')?.scrollIntoView?.({ block: 'nearest' });
  }, [highlightIndex, filtered]);

  // The transition mounts the input after opening. Focus at that commit so
  // keyboard input works immediately, including a quick close and reopen.
  const setSearchInput = useCallback((element: HTMLInputElement | null) => {
    if (element && isOpen && !hideSearchInput) element.focus();
  }, [isOpen, hideSearchInput]);

  // Outside click + Esc to close. The optional `outsideClickIgnoreRef` lets a
  // parent (e.g. AddFieldButton's trigger) opt out of being treated as
  // "outside" so clicking the trigger toggles instead of closing-then-opening.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (outsideClickIgnoreRef?.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.isComposing) {
        onClose();
        anchorRef?.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose, outsideClickIgnoreRef, anchorRef]);

  const handleSelect = useCallback(
    (option: FieldTypeOption) => {
      if (!option.enabled) return;
      if (option.type === 'section') { onAddSection?.(); return; }
      onSelect(option.type, option.format === 'text' && effectiveQuery.includes('long') ? 'text_expanded' : option.format);
    },
    [onSelect, onAddSection, effectiveQuery],
  );

  // Arrow Up/Down + Enter navigation inside the embedded input. Slash mode
  // (no embedded input) handles arrow keys at the textarea level via its own
  // keyboard handler since focus stays in the textarea.
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.nativeEvent.isComposing) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((i) => Math.min(i + 1, filtered.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const option = filtered[highlightIndex];
        if (isGenerating) return;
        if (option) handleSelect(option);
        else void handleGenerate();
      }
    },
    [filtered, highlightIndex, handleSelect, handleGenerate, isGenerating],
  );

  const menu = (
    <Transition show={isOpen} appear as={React.Fragment}>
    <div
      ref={containerRef}
      data-testid="field-type-menu"
      aria-hidden={!isOpen}
      inert={!isOpen}
      style={anchorRef ? position : undefined}
      className={`z-[80] flex w-80 flex-col rounded-xl border border-border bg-surface shadow-xl overflow-hidden transition duration-200 data-closed:opacity-0 data-closed:translate-y-1 data-closed:scale-[0.98] data-leave:duration-150 motion-reduce:transition-none ${className ?? ''}`}
    >
      {!hideSearchInput && (
        <div className="shrink-0 border-b border-border p-2">
          <input
            ref={setSearchInput}
            type="text"
            value={internalQuery}
            onChange={(e) => setInternalQuery(e.target.value)}
            onKeyDown={handleInputKeyDown}
            placeholder={onGenerate ? 'Search or describe a block' : 'Type to search'}
            aria-label={onGenerate ? 'Search or describe a block' : 'Search question types'}
            disabled={isGenerating}
            data-testid="field-type-menu-search"
            className="w-full rounded-lg bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-volt"
          />
        </div>
      )}

      <AnimatedPresence show={Boolean(onGenerate && effectiveQuery)} className="shrink-0">
        <div className="shrink-0 border-b border-border p-2">
          <button type="button" onClick={() => void handleGenerate()} disabled={isGenerating} aria-busy={isGenerating}
            className="flex w-full items-center gap-2 rounded-lg bg-volt/15 px-3 py-2 text-sm font-medium text-foreground hover:bg-volt/25 disabled:opacity-60 cursor-pointer">
            <ButtonContent loading={isGenerating}>
              <IconMessageQuestion aria-hidden="true" className="h-4 w-4" />
              Add block
            </ButtonContent>
          </button>
        </div>
      </AnimatedPresence>
      <AnimatedPresence show={Boolean(generationError)} className="shrink-0"><div role="alert" className="px-3 py-2 text-xs text-foreground">{generationError}</div></AnimatedPresence>
      {(!onGenerate || filtered.length > 0) && <div role="listbox" aria-label="Question types" className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-1">
        <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Basic blocks
        </div>
        {filtered.length === 0 ? (
          <div className="px-3 py-2 text-xs text-muted-foreground">No matches</div>
        ) : (
          <ul>
            {filtered.map((option, idx) => {
              const isHighlighted = idx === highlightIndex;
              return (
                <li key={option.format ?? option.type}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isHighlighted}
                    disabled={!option.enabled || isGenerating}
                    onMouseEnter={() => setHighlightIndex(idx)}
                    onClick={() => handleSelect(option)}
                    className={`flex w-full items-center gap-3 px-3 py-2 text-left transition-colors ${
                      option.enabled
                        ? `cursor-pointer ${isHighlighted ? 'bg-surface-raised text-foreground' : 'text-foreground hover:bg-surface-hover'}`
                        : 'cursor-not-allowed text-muted-foreground'
                    }`}
                  >
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground">
                      {option.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium">{option.label}</span>
                    </span>
                    {!option.enabled && (
                      <span
                        data-testid="phase-2-badge"
                        className="shrink-0 rounded-lg bg-surface-raised px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                      >
                        Phase 2
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>}

      <div className="flex shrink-0 items-center justify-between border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
        <button
          type="button"
          onClick={onClose}
          className="hover:text-muted-foreground cursor-pointer"
        >
          Close menu
        </button>
        <span className="rounded-lg bg-surface-raised px-1.5 py-0.5 text-[10px] text-muted-foreground">
          esc
        </span>
      </div>
    </div>
    </Transition>
  );
  return anchorRef ? createPortal(menu, document.body) : menu;
};
